import { useState, useEffect, useRef, useCallback } from 'react';
import LivechatService from '../../services/LivechatService';

interface UseAutocompleteProps {
	orgId: string;
}

interface UseAutocompleteReturn {
	suggestions: string[];
	isLoading: boolean;
	isOpen: boolean;
	focusedIndex: number;
	handleInputChange: (value: string) => void;
	handleInputKeyDown: (event: KeyboardEvent) => void;
	handleSelect: () => void;
	handleClose: () => void;
	anchorRef: React.MutableRefObject<HTMLDivElement | null>;
	setAnchorRef: (ref: HTMLDivElement | null) => void;
	setFocusedIndex: (index: number) => void;
}

/**
 * 自動完成 Hook
 * 提供輸入建議、鍵盤導航、文字高亮等功能
 */
export const useAutocomplete = ({ orgId }: UseAutocompleteProps): UseAutocompleteReturn => {
	// 狀態管理
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);

	// Anchor ref 用於定位選單
	const anchorRef = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;

	// Debounce timeout ref
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	/**
	 * 處理選擇項目
	 */
	const handleSelect = useCallback(() => {
		setIsOpen(false);
		setFocusedIndex(-1);
		setSuggestions([]);
	}, []);

	/**
	 * 關閉選單
	 */
	const handleClose = useCallback(() => {
		setIsOpen(false);
		setFocusedIndex(-1);
	}, []);

	/**
	 * API 調用 - 搜索關鍵字
	 */
	const fetchSuggestions = useCallback(
		async (keyword: string): Promise<void> => {
			if (keyword.length < 2) {
				setSuggestions([]);
				setIsOpen(false);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			console.log('[Autocomplete] Fetching suggestions — orgId:', orgId, '| keyword:', keyword);

			try {
				const data = await LivechatService.searchKeywords(orgId, {
					keyword,
					limit: 15,
					caseSensitive: false,
				});

				console.log('[Autocomplete] API response:', data);
				setSuggestions(data.keywords || []);
				setIsOpen((data.keywords || []).length > 0);
				setFocusedIndex(-1);
			} catch (error) {
				console.error('[Autocomplete] API error:', error);
				// 靜默處理錯誤
				setSuggestions([]);
				setIsOpen(false);
			} finally {
				setIsLoading(false);
			}
		},
		[orgId]
	);

	/**
	 * 處理輸入變更（帶 debounce）
	 */
	const handleInputChange = useCallback(
		(value: string) => {
			// 清除之前的 timeout
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}

			// 設定新的 debounce timeout
			debounceTimeoutRef.current = setTimeout(() => {
				fetchSuggestions(value);
			}, 300);
		},
		[fetchSuggestions]
	);

	/**
	 * 處理鍵盤事件
	 */
	const handleInputKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isOpen || suggestions.length === 0) return;

			switch (event.key) {
				case 'ArrowDown':
					event.preventDefault();
					// 向下移動焦點（不循環）
					setFocusedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
					break;

				case 'ArrowUp':
					event.preventDefault();
					// 向上移動焦點（不循環）
					setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1));
					break;

				case 'Enter':
					event.preventDefault();
					// 選擇當前焦點項目
					if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
						handleSelect();
					}
					break;

				case 'Escape':
					event.preventDefault();
					handleClose();
					break;

				default:
					break;
			}
		},
		[isOpen, suggestions.length, focusedIndex, handleSelect, handleClose]
	);

	// 設定 Anchor ref
	const setAnchorRef = useCallback((ref: HTMLDivElement | null) => {
		anchorRef.current = ref;
	}, []);

	// 清理 debounce timeout
	useEffect(() => {
		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, []);

	return {
		suggestions,
		isLoading,
		isOpen,
		focusedIndex,
		handleInputChange,
		handleInputKeyDown,
		handleSelect,
		handleClose,
		anchorRef,
		setAnchorRef,
		setFocusedIndex,
	};
};

export default useAutocomplete;
